import { ReactElement, useContext } from "react";

import classNames from "classnames";

import { DataSource } from "../DataSource";
import { SortDirection, Sortable } from "../Sortable";
import { BaseColumnProps } from "./BaseColumnProps";
import { ColumnElementConstructor } from "./ColumnElementConstructor";
import { TableColumnHeader } from "./TableColumnHeader";
import { TableColumnHeaderTail } from "./TableColumnHeaderTail";
import { TableLayoutContext } from "./TableLayoutContext";

import styles from "./styles.module.scss";

export interface ColumnWidthLookupTable {
  readonly [key: string]: number;
}

/** The `TableHeader` component properties. */
export interface TableHeaderProps<TRow extends object> {
  /**
   * The data source.
   */
  readonly dataSource: DataSource<TRow> & Sortable;

  /**
   * The column ref function.
   */
  readonly columnRef?: (key: string, ref: HTMLTableCellElement | null) => void;
}

/**
 * Renders a set of columns.
 * @param dataSource The current data source.
 * @param columns The set of columns to render.
 * @param columnRef The optional column reference handler.
 * @param offsetProvider The optional column offset provider.
 * @returns The set of column elements.
 */
function renderColumns<TRow extends object>(
  dataSource: DataSource<TRow> & Sortable,
  columns: ReadonlyArray<ReactElement<BaseColumnProps>>,
  columnRef?: (key: string, ref: HTMLTableCellElement | null) => void,
  offsetProvider?: (index: number) => number,
) {
  const { sorted, sortBy, getSortDirection } = dataSource;

  return columns.map((column, index) => {
    const { props, type } = column;
    const { name, sortable = false } = props;

    const transitionLeft = index === 0;
    const transitionRight = index === columns.length - 1;

    let sortDirection: SortDirection | null = null;
    if (sortable && sorted) {
      if (typeof sortable === "string") sortDirection = getSortDirection(sortable);
      else sortDirection = getSortDirection(name);
    }

    let className = props.className;
    const headerAlignment = (type as ColumnElementConstructor<unknown>).headerAlignment;

    const align: BaseColumnProps["align"] = props.align ?? headerAlignment ?? "left";

    if (typeof type !== "string") {
      className = classNames(className, (type as ColumnElementConstructor<unknown>).className);
    }

    return (
      <TableColumnHeader
        {...props}
        key={name}
        innerRef={columnRef}
        className={className}
        transitionLeft={transitionLeft}
        transitionRight={transitionRight}
        sortDirection={sortDirection}
        onClick={sortBy}
        align={align}
        position={offsetProvider ? offsetProvider(index) : undefined}
      />
    );
  });
}

/**
 * The table header component.
 */
export const TableHeader = <TRow extends object>(props: TableHeaderProps<TRow>) => {
  const { dataSource, columnRef } = props;

  const { leftColumns, middleColumns, rightColumns, leftOffsets, rightOffsets } = useContext(TableLayoutContext);

  return (
    <thead className={classNames(styles.TableHeader, "table-header")}>
      <tr className="columns-container">
        {renderColumns(dataSource, leftColumns, columnRef, (index) => leftOffsets[index])}
        {renderColumns(dataSource, middleColumns, columnRef)}
        {renderColumns(dataSource, rightColumns, columnRef, (index) => rightOffsets[rightOffsets.length - (index + 1)])}
        <TableColumnHeaderTail />
      </tr>
    </thead>
  );
};
